import { useCallback, useContext, useEffect } from "react";
import { useMachine, useSelector } from "@xstate/react";

import { RefreshContext } from "./context";
import { RefreshAction, RefreshActionRef, RefreshTimer } from "./types";
import { refreshMachine, RefreshMachineInput } from "./machine";

export const useRefreshTimer = (input: RefreshMachineInput): RefreshTimer => {
  const [state, send, machine] = useMachine(refreshMachine, { input });
  const controller = useSelector(machine, (state) => state.context.controller);
  const refreshNow = useCallback(() => send({ type: "REFRESH_NOW" }), [send]);
  const registerRefreshAction = useCallback(
    (action: RefreshAction) => controller.register(action),
    [controller],
  );
  const cleanupRefreshAction = useCallback(
    (ref: RefreshActionRef) => controller.cleanup(ref),
    [controller],
  );

  return {
    isActive: state.matches("active"),
    isRefreshing: state.matches({ active: "refreshing" }),
    lastRefreshedAt: useSelector(machine, (state) => state.context.lastRefreshedAt),
    autoRefreshInterval: useSelector(machine, (state) => state.context.autoRefreshInterval),
    refreshNow,
    registerRefreshAction,
    cleanupRefreshAction,
  };
};

export const useRefreshAction = (action: RefreshAction) => {
  const refreshTimer = useContext(RefreshContext);
  const { registerRefreshAction, cleanupRefreshAction } = refreshTimer || {};

  useEffect(() => {
    if (registerRefreshAction && cleanupRefreshAction) {
      const ref = registerRefreshAction(action);
      return () => cleanupRefreshAction(ref);
    }
  }, [registerRefreshAction, cleanupRefreshAction, action]);
};
